import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import { customerAPI } from '../../services/api';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const CreateCustomer = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const { register, handleSubmit, watch, setError, formState: { errors } } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      father_name: '',
      guardian_name: '',
      gender: 'male',
      date_of_birth: '',
      mobile_1: '',
      mobile_2: '',
      email: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      pincode: '',
      id_proof_type: '',
      id_proof_number: ''
    }
  });

  const idProofType = watch('id_proof_type');

  const idProofValidators = (() => {
    switch (idProofType) {
      case 'aadhar':
        return {
          pattern: {
            value: /^\d{12}$/,
            message: 'Aadhar must be 12 digits',
          },
        };
      case 'pan':
        return {
          pattern: {
            value: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
            message: 'PAN must be 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)',
          },
        };
      case 'voter_id':
        return {
          pattern: {
            value: /^[A-Z]{3}[0-9]{7}$/,
            message: 'Voter ID must be 3 letters followed by 7 digits',
          },
        };
      case 'driving_license':
        return {
          pattern: {
            value: /^[A-Z]{2}[0-9]{2}\s?[0-9A-Z]{7,12}$/,
            message: 'DL must start with state code (e.g., KA01) followed by 7-12 chars',
          },
        };
      case 'passport':
        return {
          pattern: {
            value: /^[A-PR-WY]{1}[0-9]{7}$/i,
            message: 'Passport must be 1 letter followed by 7 digits',
          },
        };
      default:
        return {};
    }
  })();

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await customerAPI.createCustomer(data);
      // Refresh customers list without reload
      queryClient.invalidateQueries(['customers']);
      navigate('/customers');
    } catch (e) {
      const resp = e?.response?.data;
      if (Array.isArray(resp?.errors)) {
        const LABELS = {
          first_name: 'First Name',
          last_name: 'Last Name',
          father_name: "Father's Name",
          guardian_name: "Guardian's Name",
          gender: 'Gender',
          date_of_birth: 'Date of Birth',
          mobile_1: 'Mobile Number 1',
          mobile_2: 'Mobile Number 2',
          email: 'Email',
          address_line_1: 'Address Line 1',
          address_line_2: 'Address Line 2',
          city: 'City',
          state: 'State',
          pincode: 'Pincode',
          id_proof_type: 'ID Proof Type',
          id_proof_number: 'ID Proof Number',
        };
        resp.errors.forEach((err) => {
          if (err?.field && err?.message) {
            setError(err.field, { type: 'server', message: err.message });
            const fieldLabel = LABELS[err.field] || err.field;
            toast.error(`${fieldLabel}: ${err.message}`);
          }
        });
      } else {
        const message = resp?.message || 'Failed to create customer';
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/customers" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Customers
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-xl font-semibold text-gray-900 mb-4">Add New Customer</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input className="input mt-1" {...register('first_name', { required: 'First name is required' })} />
              {errors.first_name && <p className="text-sm text-red-600 mt-1">{errors.first_name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input className="input mt-1" {...register('last_name', { required: 'Last name is required' })} />
              {errors.last_name && <p className="text-sm text-red-600 mt-1">{errors.last_name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Father's Name</label>
              <input className="input mt-1" {...register('father_name')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Guardian's Name</label>
              <input className="input mt-1" {...register('guardian_name')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Gender</label>
              <select className="input mt-1" {...register('gender', { required: true })}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && <p className="text-sm text-red-600 mt-1">Gender is required</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
              <input type="date" className="input mt-1" {...register('date_of_birth')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile Number 1</label>
              <input className="input mt-1" {...register('mobile_1', { required: 'Primary mobile is required', pattern: { value: /^[6-9][0-9]{9}$/, message: 'Enter valid 10-digit mobile starting with 6-9' } })} />
              {errors.mobile_1 && <p className="text-sm text-red-600 mt-1">{errors.mobile_1.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile Number 2</label>
              <input className="input mt-1" {...register('mobile_2', { pattern: { value: /^(|[6-9][0-9]{9})$/, message: 'Enter valid 10-digit mobile or leave blank' } })} />
              {errors.mobile_2 && <p className="text-sm text-red-600 mt-1">{errors.mobile_2.message}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input type="email" className="input mt-1" {...register('email', { pattern: { value: /^(|[^\s@]+@[^\s@]+\.[^\s@]+)$/i, message: 'Enter a valid email address' } })} />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
              <input className="input mt-1" {...register('address_line_1', { required: 'Address is required' })} />
              {errors.address_line_1 && <p className="text-sm text-red-600 mt-1">{errors.address_line_1.message}</p>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
              <input className="input mt-1" {...register('address_line_2')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input className="input mt-1" {...register('city', { required: 'City is required' })} />
              {errors.city && <p className="text-sm text-red-600 mt-1">{errors.city.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <input className="input mt-1" {...register('state', { required: 'State is required' })} />
              {errors.state && <p className="text-sm text-red-600 mt-1">{errors.state.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pincode</label>
              <input className="input mt-1" {...register('pincode', { required: 'Pincode is required', pattern: { value: /^[1-9][0-9]{5}$/, message: 'Enter a valid 6-digit PIN code' } })} />
              {errors.pincode && <p className="text-sm text-red-600 mt-1">{errors.pincode.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">ID Proof Type</label>
              <select className="input mt-1" {...register('id_proof_type')}>
                <option value="">Select ID Proof Type</option>
                <option value="aadhar">Aadhar</option>
                <option value="pan">PAN</option>
                <option value="voter_id">Voter ID</option>
                <option value="driving_license">Driving License</option>
                <option value="passport">Passport</option>
              </select>
              {errors.id_proof_type && <p className="text-sm text-red-600 mt-1">{errors.id_proof_type.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">ID Proof Number</label>
              <input className="input mt-1" {...register('id_proof_number', { ...idProofValidators })} />
              {errors.id_proof_number && <p className="text-sm text-red-600 mt-1">{errors.id_proof_number.message}</p>}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Link to="/customers" className="px-4 py-2 border rounded">Cancel</Link>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50">
              {submitting ? 'Saving...' : 'Save Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCustomer;


